import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Check } from "lucide-react";
import { API, Shell, fmtTime, useT } from "./Shell";
import "./testTheme.css";

export default function TestStatus() {
  const { studentId } = useParams();
  const T = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const load = () => axios.get(`${API}/test/status/${studentId}`).then(({ data: d }) => setData(d)).catch(() => setError("notfound"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [studentId]);

  const cancel = async () => {
    if (!window.confirm(T.s_cancel_confirm)) return;
    setCancelling(true);
    try { await axios.post(`${API}/test/booking/${data.booking.booking_id}/cancel`); await load(); } catch { setError("cancel"); } finally { setCancelling(false); }
  };

  if (error) return <Shell><div className="tp-wrap"><div className="tp-hero"><p className="tp-error" data-testid="tp-status-error">{error === "cancel" ? T.s_cancel_err : T.s_notfound}</p></div></div></Shell>;
  if (!data) return <Shell><div className="tp-wrap"><div className="tp-hero"><p className="tp-muted">{T.s_loading}</p></div></div></Shell>;

  const { student, attempt, attempts = [], reward, booking } = data;
  const steps = [
    [T.r_track[0], !!attempt],
    [T.r_track[1], !!booking?.consultation_done],
    [T.r_track[2], !!booking?.viva_done],
    [T.r_track[3], ["Approved for Payment", "Payment Completed"].includes(reward?.status)],
    [T.r_track[4], reward?.status === "Payment Completed"],
  ];

  return (
    <Shell>
      <div className="tp-wrap" style={{ maxWidth: 760, padding: "50px 22px 70px" }}>
        <div className="tp-eyebrow">{T.s_eyebrow}</div>
        <h1 className="tp-h2" style={{ fontSize: 34 }} data-testid="tp-status-title">{T.s_greeting(student.name.split(" ")[0])}</h1>
        <div className="tp-panel" style={{ marginTop: 18 }}>
          <div className="tp-detail-grid" data-testid="tp-status-grid">
            <div>{T.s_name}<b>{student.name}</b></div>
            <div>{T.b_sid}<b>{student.student_id}</b></div>
            <div>{T.s_result}<b>{attempt ? `${attempt.result.correct}/5 ${T.r_correct.toLowerCase()} · ${attempt.result.score}/${attempt.result.max_score} · ${fmtTime(attempt.result.time_taken_sec, T.r_min)}` : T.s_notyet}</b></div>
            {attempt?.topic_name && <div>{T.s_topic}<b>{attempt.topic_name}</b></div>}
            <div>{T.s_elig}<b>{reward ? T.s_eligible : attempt ? T.s_noteligible : "—"}</b></div>
            {reward && <div>{T.s_amount}<b style={{ color: "#f77f00" }}>₹{reward.amount}</b></div>}
            <div>{T.s_consult}<b>{booking ? (booking.consultation_done ? T.s_completed : `${T.s_booked} ${booking.date} ${booking.slot}`) : T.s_notbooked}</b></div>
            <div>{T.s_viva}<b>{booking?.viva_done ? T.s_completed : T.s_pending}</b></div>
            {reward && <div>{T.s_pay}<b>{reward.status}</b></div>}
          </div>
          {attempts.length > 0 && (
            <div style={{ marginTop: 20 }} data-testid="tp-topic-results">
              <h4 style={{ margin: "0 0 10px", fontSize: 13, letterSpacing: "0.06em", color: "#5b6b82" }}>{T.s_topics_title}</h4>
              {attempts.map((a, i) => (
                <div key={a.attempt_id} className="tp-analysis-card" style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", marginBottom: 8 }} data-testid={`tp-attempt-${i}`}>
                  <span style={{ flex: 1, fontWeight: 600 }}>{a.topic_name}</span>
                  <span className="tp-pill info">{a.result.score}/{a.result.max_score} · {a.result.correct}/5 {T.r_correct.toLowerCase()}</span>
                  <a className="tp-back" href={`/physics-test/result/${a.attempt_id}`} data-testid={`tp-attempt-view-${i}`}>{T.tp_view_result}</a>
                </div>
              ))}
              <div style={{ marginTop: 8 }}><a className="tp-btn ghost small" href="/physics-test/topics" data-testid="tp-status-topics">{T.r_explore}</a></div>
            </div>
          )}
          {reward && <p className="tp-pill info" style={{ marginTop: 6 }} data-testid="tp-status-message">{T.pay_messages[reward.status] || reward.status}</p>}
          {reward && <div className="tp-track" style={{ marginTop: 22 }}>{steps.map(([label, done], i) => (
            <div key={label} style={{ display: "flex", alignItems: "center", flex: i < steps.length - 1 ? 1 : "0" }}>
              <div className={done ? "node done" : "node"}><div className="dot">{done ? <Check size={15} /> : i + 1}</div><span>{label}</span></div>
              {i < steps.length - 1 && <div className={steps[i + 1][1] ? "link done" : "link"} />}
            </div>
          ))}</div>}
          {!reward && attempt && <p className="tp-muted" style={{ marginTop: 10 }}>{T.s_still}</p>}
          <div style={{ display: "flex", gap: 10, marginTop: 22, flexWrap: "wrap" }}>
            {!booking && <a className="tp-btn small" href={`/physics-test/book/${student.student_id}`} data-testid="tp-status-book">{reward ? T.s_book : T.s_book_free}</a>}
            {booking && booking.status === "booked" && <button className="tp-btn ghost small" onClick={cancel} disabled={cancelling} data-testid="tp-status-cancel">{cancelling ? T.s_cancelling : T.s_cancel}</button>}
          </div>
        </div>
      </div>
    </Shell>
  );
}
